import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { VendorRequestType } from '@leanpay/models'
import { checkoutService } from '@leanpay/services'

import { useAppStore } from './app-store'
import {
    COOKIES_CONSENT,
    RawCookies,
} from 'components/manage-cookies-consent/manage-cookies-consent'
import { useGetSessionInfo } from 'library/hooks/query-hooks'
import { useRouteTracker } from 'library/hooks/use-step-tracker'
import { isStandalonePublicRoute } from 'library/utils'

const AppInit = ({ children }: { children: ReactNode }) => {
    const {
        setVendorToken,
        setVendorData,
        setIsAuthenticated,
        setIsCookieConsentEnabled,
        lastRecordedRoute,
        canAccessRoute,
        resetStore,
        vendorToken,
    } = useAppStore()
    const { data: sessionInfoData } = useGetSessionInfo()
    const navigate = useNavigate()
    const location = useLocation()
    const params = new URLSearchParams(window.location.search)
    const isInitialLoad = useRef(true)

    // Track route changes
    useRouteTracker()

    const handleInitVendorToken = useCallback(async () => {
        resetStore()
        try {
            const vendorInitApiData = await checkoutService.getVendorTokenPQ({
                vendor: params.get('vendor') ?? undefined,
                returnUrl: params.get('returnUrl') ?? undefined,
                requestType: VendorRequestType.WEB,
            })
            if (vendorInitApiData.token) {
                setVendorToken(vendorInitApiData.token)
                const preQualifiedInfo = await checkoutService.getPreQualifiedInfo({
                    token: vendorInitApiData.token,
                })

                setVendorData(preQualifiedInfo)
                navigate('/check-limit', { replace: true })
                return
            } else {
                navigate('/prequalified-error', {
                    state: { error: 'DATA_NOT_VALID' },
                    replace: true,
                })
            }
        } catch {
            navigate('/prequalified-error', { replace: true, state: { error: 'DATA_NOT_VALID' } })
        }
    }, [navigate, params, resetStore, setVendorData, setVendorToken])

    const handleSetCookieConsent = useCallback(() => {
        const cookieConsent = RawCookies.get(COOKIES_CONSENT)
        setIsCookieConsentEnabled(!!cookieConsent)
    }, [setIsCookieConsentEnabled])

    useEffect(() => {
        if (location.pathname === '/' && !isStandalonePublicRoute) {
            try {
                void handleInitVendorToken()
            } catch {
                navigate('/prequalified-error', {
                    replace: true,
                    state: { error: 'DATA_NOT_VALID' },
                })
            }
        }
    }, [location.pathname])

    useEffect(() => {
        if (sessionInfoData) {
            setIsAuthenticated(true)
        }
    }, [sessionInfoData, setIsAuthenticated])

    useEffect(() => {
        handleSetCookieConsent()
    }, [handleSetCookieConsent])

    // Route validation on initial load/refresh
    useEffect(() => {
        if (isInitialLoad.current && lastRecordedRoute && location.pathname !== '/') {
            isInitialLoad.current = false

            // If current route !== last recorded route, kick them out
            if (!canAccessRoute(location.pathname)) {
                const handleInvalidAccess = async () => {
                    try {
                        if (vendorToken) {
                            await checkoutService.invalidateVendorRequest({ token: vendorToken })
                        }
                    } catch {
                        console.error()
                    } finally {
                        resetStore()
                        navigate('/', { replace: true })
                    }
                }
                void handleInvalidAccess()
            }
        }
    }, [location.pathname, lastRecordedRoute, canAccessRoute, vendorToken, resetStore, navigate])

    return children
}

export default AppInit
